import type {
  ClaudeCredential,
  ClaudeCredentialType,
  CreateClaudeCredentialInput,
} from '@mss_creator/shared';
import { Button, TextField } from '@pms-digital/design-system';
import { useState } from 'react';

export interface ClaudeCredentialManagerProps {
  credentials: ClaudeCredential[];
  onAdd: (input: CreateClaudeCredentialInput) => Promise<void>;
  onActivate: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

/**
 * pms-only admin surface (see docs/superpowers/specs/2026-08-11-claude-credentials-admin-design.md):
 * list stored credentials (never their secret), add a new one (validated
 * server-side before it's persisted), activate one, delete one (blocked
 * server-side, and disabled here, while it's active).
 */
export function ClaudeCredentialManager({
  credentials,
  onAdd,
  onActivate,
  onDelete,
}: ClaudeCredentialManagerProps) {
  const [alias, setAlias] = useState('');
  const [type, setType] = useState<ClaudeCredentialType>('oauth_token');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await onAdd({ alias: alias.trim(), type, secret });
      setAlias('');
      setType('oauth_token');
      setSecret('');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Erreur inconnue.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleActivate(id: string) {
    setRowError(null);
    onActivate(id).catch((activateError: unknown) => {
      setRowError(
        activateError instanceof Error ? activateError.message : "Erreur lors de l'activation.",
      );
    });
  }

  function handleDelete(id: string) {
    setRowError(null);
    onDelete(id).catch((deleteError: unknown) => {
      setRowError(
        deleteError instanceof Error ? deleteError.message : 'Erreur lors de la suppression.',
      );
    });
  }

  return (
    <div className="space-y-8">
      {rowError && <p className="text-danger text-small">{rowError}</p>}

      <ul className="space-y-2">
        {credentials.map((credential) => (
          <li
            key={credential.id}
            className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-4 py-3"
          >
            <div className="flex items-center gap-3">
              <span className="font-semibold">{credential.alias}</span>
              <span className="text-caption text-gray-500 uppercase">{credential.type}</span>
              {credential.isActive && (
                <span className="rounded-full bg-success/10 px-2 py-0 font-semibold text-caption text-success">
                  Actif
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {!credential.isActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleActivate(credential.id)}
                >
                  Activer
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={credential.isActive}
                onClick={() => handleDelete(credential.id)}
              >
                Supprimer
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <TextField
            label="Alias"
            id="claude-credential-alias"
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
          />
        </div>

        <div>
          <label htmlFor="claude-credential-type" className="font-semibold text-small">
            Type
          </label>
          <select
            id="claude-credential-type"
            className="mt-1 block h-11 w-full rounded-md border-2 border-gray-200 bg-white px-4 text-body transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
            value={type}
            onChange={(event) => setType(event.target.value as ClaudeCredentialType)}
          >
            <option value="oauth_token">Compte Claude personnel (oauth_token)</option>
            <option value="api_key">Clé API (api_key)</option>
          </select>
        </div>

        <div>
          <TextField
            label="Secret"
            id="claude-credential-secret"
            type="password"
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
          />
        </div>

        {error && <p className="text-danger text-small">{error}</p>}

        <Button variant="secondary" type="submit" disabled={isSubmitting}>
          Ajouter
        </Button>
      </form>
    </div>
  );
}
